import { CheckrNode, CheckrPackage } from '@dadohr/shared/domainTypes/checkr';
import { UnsafeTFunction } from '../../../../../../i18next';
import {
  callFirebaseFunction,
  useFirebaseFunction
} from '../../../../../../services/firebaseFunctions';
import { ONE_MINUTE } from '../../../../../../services/time';

export type CheckrConnectionStatus = {
  connected: boolean;
};

export type {
  CheckrNode,
  CheckrPackage,
  CheckrScreening
} from '@dadohr/shared/domainTypes/checkr';

export const useCheckrConnectionStatus = (orgId: string) =>
  useFirebaseFunction<CheckrConnectionStatus>(
    'checkr_getConnectionStatus',
    { orgId },
    [orgId]
  );

export const useCheckrPackages = (orgId: string) => {
  const [data, loading, error, refresh] = useFirebaseFunction<{
    packages: CheckrPackage[];
    nodes: CheckrNode[];
  }>('checkr_listPackages', { orgId }, [orgId], ONE_MINUTE * 15);
  return {
    packages: data?.packages ?? [],
    nodes: data?.nodes ?? [],
    loading,
    error,
    refresh
  };
};

export const connectCheckr = async (
  orgId: string,
  apiKey: string,
  t: UnsafeTFunction
): Promise<void> => {
  try {
    await callFirebaseFunction('checkr_verifyCredentialsAndStore', {
      orgId,
      apiKey
    });
  } catch (err) {
    throw new Error(mapCheckrError(err, t));
  }
};

export const disconnectCheckr = (orgId: string) =>
  callFirebaseFunction<{ success: true }>('checkr_disconnect', { orgId });

/**
 * Retry a failed background-check invitation for a single sub-task. Re-queues
 * the aborted BACKGROUND_CHECK_INVITE external op and clears the
 * CANNOT_SEND_BACKGROUND_CHECK instanceError server-side; the worker's next
 * sweep re-runs the invitation with the current package + participant data.
 */
export const retrySendBackgroundCheck = (input: {
  orgId: string;
  experienceId: string;
  subtaskTplId: string;
}) =>
  callFirebaseFunction<{ success: true; requeuedOpCount: number }>(
    'checkr_retryBackgroundCheckInvitation',
    input
  );

const mapCheckrError = (err: unknown, t: UnsafeTFunction): string => {
  const e = err as { code?: string; message?: string };
  const code = e?.code ?? '';
  const backendMessage = e?.message;

  switch (code) {
    case 'invalid-argument':
      return (
        backendMessage ??
        t('The API key you provided is invalid. Please check it and try again.')
      );
    case 'permission-denied':
      return (
        backendMessage ??
        t(
          'Your Checkr account is not authorized. Please check your account status in the Checkr dashboard.'
        )
      );
    case 'unavailable':
      return (
        backendMessage ??
        t('Unable to connect to Checkr. Please try again later.')
      );
    case 'internal':
      return (
        backendMessage ?? t('Failed to save credentials. Please try again.')
      );
    default:
      return t('Unable to connect to Checkr. Please try again later.');
  }
};
